import { Box, Chip, IconButton, Tooltip, Typography } from '@mui/material'
import OpenInFullIcon from '@mui/icons-material/OpenInFull'
import { ReactElement } from 'react'
import { useTranslation } from 'react-i18next'
import { IListItemValue } from '../../interfaces/Interfaces'
import TruncatedText from '../Common/TruncatedText'

interface OrgCompactFieldProps {
    fieldId: string
    label: string
    manageLabel: string
    value: IListItemValue[]
    onExpand: () => void
    disabled: boolean
    added?: number
    removed?: number
}

const OrgCompactField = ({
    fieldId,
    label,
    manageLabel,
    value,
    onExpand,
    disabled,
    added = 0,
    removed = 0,
}: OrgCompactFieldProps): ReactElement => {
    const { t } = useTranslation()
    return (
        <Box sx={{ flex: 1, minWidth: 0 }}>
            <Box
                sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 0.75,
                }}
            >
                <Typography sx={{ fontSize: 14, color: 'text.secondary' }}>
                    {label}
                </Typography>
                {added > 0 && (
                    <Chip
                        label={`+${added}`}
                        size="small"
                        sx={{
                            height: 16,
                            fontSize: 10,
                            bgcolor: '#e6f4ea',
                            color: '#1e7e34',
                            border: '1px solid #a8d5b5',
                            '& .MuiChip-label': { px: 0.75 },
                        }}
                    />
                )}
                {removed > 0 && (
                    <Chip
                        label={`-${removed}`}
                        size="small"
                        sx={{
                            height: 16,
                            fontSize: 10,
                            bgcolor: '#fdecea',
                            color: '#c62828',
                            border: '1px solid #f5c6cb',
                            '& .MuiChip-label': { px: 0.75 },
                        }}
                    />
                )}
            </Box>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                <Box
                    id={`program-${fieldId}-field`}
                    sx={{
                        flex: 1,
                        minWidth: 0,
                        border: `1px solid ${added > 0 || removed > 0 ? '#00797a' : 'rgba(0,0,0,0.23)'}`,
                        boxShadow:
                            added > 0 || removed > 0
                                ? '0 0 0 1px rgba(0,121,122,0.25)'
                                : 'none',
                        borderRadius: '4px',
                        px: 1.5,
                        py: 0.75,
                        minHeight: 37,
                        display: 'flex',
                        alignItems: 'center',
                        bgcolor: disabled
                            ? 'rgba(0,0,0,0.04)'
                            : added > 0 || removed > 0
                              ? '#f4fafa'
                              : '#fff',
                        overflow: 'hidden',
                        cursor: disabled ? 'default' : 'pointer',
                    }}
                    onClick={disabled ? undefined : onExpand}
                >
                    <TruncatedText
                        sx={{
                            fontSize: 13,
                            width: '100%',
                            color: value?.length
                                ? 'text.primary'
                                : 'text.disabled',
                        }}
                        tooltipTitle={
                            value?.length
                                ? value.map((v) => v.name).join(', ')
                                : undefined
                        }
                    >
                        {value?.length
                            ? value.map((v) => v.name).join(', ')
                            : t('selectOrgPlaceholder', { label })}
                    </TruncatedText>
                </Box>
                {!disabled && (
                    <Tooltip title={manageLabel} placement="top" arrow>
                        <IconButton
                            size="small"
                            onClick={onExpand}
                            id={`program-${fieldId}-manage-button`}
                            aria-label={manageLabel}
                            sx={{
                                border: '1px solid rgba(0,0,0,0.23)',
                                borderRadius: '4px',
                                p: 0.5,
                                flexShrink: 0,
                            }}
                        >
                            <OpenInFullIcon sx={{ fontSize: 15 }} />
                        </IconButton>
                    </Tooltip>
                )}
            </Box>
        </Box>
    )
}

export default OrgCompactField
